import { useMemo } from 'react';
import { ContentCopy, Refresh } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type {
  RawUploadGroup,
  ResourceImportSessionSummary,
} from '../store/hooks';

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';

/** A single migration unit — one upload and the batch it converts into.
 *  Upload and batch share an id by convention (`batch_id == upload_id`), so a
 *  unit carries whichever of the two exists at this point in the lifecycle. */
interface MigrationUnit {
  id: string;
  label: string;
  fileCount?: number;
  sizeBytes?: number;
  rawPrefix?: string;
  sessionId?: string;
  status?: string;
  stats?: Record<string, number>;
  createdAt?: string | null;
}

interface MigrationImportsListProps {
  uploads: RawUploadGroup[];
  sessions: ResourceImportSessionSummary[];
  isLoading: boolean;
  onRefresh: () => void;
  onOpenBatch: (sessionId: string) => void;
  /** Add more files to a still-pre-conversion batch (raw upload id). */
  onAddFiles: (uploadId: string) => void;
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** The trailing `<batch_id>/` segment of a converted prefix === the upload id. */
function batchIdOf(s3Prefix?: string | null): string {
  if (!s3Prefix) return '';
  const parts = s3Prefix.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

/** Map the staging status onto the operator-facing lifecycle stage. */
function lifecycle(unit: MigrationUnit): { label: string; color: ChipColor } {
  if (!unit.sessionId) return { label: 'Uploaded', color: 'default' };
  switch (unit.status) {
    case 'ready_for_review':
      return { label: 'Ready to review', color: 'warning' };
    case 'importing':
      return { label: 'Committing', color: 'info' };
    case 'completed':
      return { label: 'Committed', color: 'success' };
    case 'failed':
      return { label: 'Failed', color: 'error' };
    default:
      return { label: unit.status ?? 'Staged', color: 'info' };
  }
}

/**
 * One unified list of migration units. An "upload" and the "batch" it converts
 * into are two phases of the same thing (they share an id), so each row shows a
 * single source progressing Uploaded → Ready to review → Committed, rather than
 * exposing the raw-uploads and staged-batches tables separately.
 */
export function MigrationImportsList({
  uploads,
  sessions,
  isLoading,
  onRefresh,
  onOpenBatch,
  onAddFiles,
}: MigrationImportsListProps) {
  const { enqueueSnackbar } = useSnackbar();

  const units = useMemo<MigrationUnit[]>(() => {
    const byId = new Map<string, MigrationUnit>();
    for (const u of uploads) {
      byId.set(u.upload_id, {
        id: u.upload_id,
        label: u.label || u.upload_id,
        fileCount: u.file_count,
        sizeBytes: u.total_size_bytes,
        rawPrefix: u.prefix,
      });
    }
    for (const s of sessions) {
      const id = batchIdOf(s.s3_prefix);
      if (!id) continue;
      const existing = byId.get(id) ?? { id, label: id };
      byId.set(id, {
        ...existing,
        sessionId: s.import_session_id,
        status: s.status,
        stats: s.import_stats ?? undefined,
        createdAt: s.created_at,
      });
    }
    // Newest first when we have a timestamp; otherwise stable by label.
    return [...byId.values()].sort((a, b) => {
      if (a.createdAt && b.createdAt) return b.createdAt.localeCompare(a.createdAt);
      if (a.createdAt) return -1;
      if (b.createdAt) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [uploads, sessions]);

  const copyPrefix = (prefix: string) => {
    void navigator.clipboard.writeText(prefix);
    enqueueSnackbar('Copied S3 prefix', { variant: 'success' });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Imports"
        subheader="Each source you upload, through its lifecycle: uploaded → converted & staged (by the migration agent) → reviewed & committed."
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} aria-label="Refresh imports">
              <Refresh />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : units.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No imports yet. Upload source files above to start one.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Files</TableCell>
                <TableCell align="right">Resources</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {units.map(unit => {
                const stage = lifecycle(unit);
                const staged = unit.stats?.staged;
                const total = unit.stats?.total;
                const openable = Boolean(unit.sessionId);
                return (
                  <TableRow key={unit.id} hover>
                    <TableCell>
                      {openable ? (
                        <MuiLink
                          component="button"
                          type="button"
                          onClick={() => onOpenBatch(unit.sessionId as string)}
                          sx={{ textAlign: 'left' }}
                        >
                          {unit.label}
                        </MuiLink>
                      ) : (
                        unit.label
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={stage.label} color={stage.color} />
                    </TableCell>
                    <TableCell align="right">{unit.fileCount ?? '—'}</TableCell>
                    <TableCell align="right">
                      {total != null ? (staged != null ? `${staged}/${total}` : total) : '—'}
                    </TableCell>
                    <TableCell align="right">{formatBytes(unit.sizeBytes)}</TableCell>
                    <TableCell>
                      {unit.createdAt
                        ? new Date(unit.createdAt).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {openable ? (
                        <MuiLink
                          component="button"
                          type="button"
                          onClick={() => onOpenBatch(unit.sessionId as string)}
                        >
                          {unit.status === 'completed' ? 'View' : 'Review & commit'}
                        </MuiLink>
                      ) : unit.rawPrefix ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                          }}
                        >
                          <MuiLink
                            component="button"
                            type="button"
                            onClick={() => onAddFiles(unit.id)}
                          >
                            Add files
                          </MuiLink>
                          <Tooltip title="Awaiting conversion — copy the S3 prefix for the migration agent">
                            <IconButton
                              size="small"
                              aria-label="Copy S3 prefix"
                              onClick={() => copyPrefix(unit.rawPrefix as string)}
                            >
                              <ContentCopy fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
